import { t } from "@lingui/macro";
import type { ErrorMessage } from "@stylingcv/utils";
import { deepSearchAndParseDates } from "@stylingcv/utils";
import _axios, { isAxiosError, type InternalAxiosRequestConfig } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { refreshToken } from "@/client/services/auth";

// Extend axios config to include custom property for auth refresh control
declare module "axios" {
  interface InternalAxiosRequestConfig {
    __skipAuthRefresh?: boolean;
  }
}

import { USER_KEY } from "../constants/query-keys";
import { toast } from "../hooks/use-toast";
import { translateError } from "../services/errors/translate-error";
import { getPersistentFingerprint } from "../utils/fingerprint";
import { queryClient } from "./query-client";

// Use environment variable for API URL, fallback to localhost for development
const apiBaseURL = import.meta.env.VITE_API_URL || "http://localhost:3000/api";
// console.log("Axios initialized with baseURL:", apiBaseURL);

// Create axios instance with proper configuration
export const axios = _axios.create({
  baseURL: apiBaseURL,
  timeout: 180_000, // 3 minute timeout for AI operations (DeepSeek can take 60-90 seconds)
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Add isAxiosError function to the axios instance
(axios as any).isAxiosError = isAxiosError;

function shouldAttachGuestFingerprint(url: string | undefined): boolean {
  if (!url) return false;
  return url.includes("/guest/") || url.includes("/ai/stream/generate");
}

axios.interceptors.request.use((config) => {
  if (typeof window === "undefined") return config;
  if (!shouldAttachGuestFingerprint(config.url)) return config;
  try {
    const fp = getPersistentFingerprint();
    if (fp) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)["X-Guest-Fingerprint"] = fp;
    }
  } catch {
    // fingerprint storage may be blocked
  }
  return config;
});

// Add request interceptor to prevent excessive requests
let requestCount = 0;
const MAX_CONCURRENT_REQUESTS = 10; // Increased to allow more concurrent requests

// Rate limiting map to prevent duplicate requests within time window
// Stores a deferred promise that resolves with the actual HTTP response
const requestThrottleMap = new Map<
  string,
  {
    lastCall: number;
    inFlight: boolean;
    promise?: Promise<any>;
    resolve?: (value: any) => void;
    reject?: (reason?: any) => void;
  }
>();
const THROTTLE_WINDOW = 500; // Reduced to 500ms for faster recovery

axios.interceptors.request.use(
  (config) => {
    // Rate limit duplicate requests to same endpoints
    if (config.url) {
      const key = `${config.method || "GET"}-${config.url}`;
      const now = Date.now();
      const existing = requestThrottleMap.get(key);

      // If there's an in-flight request to the same endpoint, wait for it instead of rejecting
      if (existing?.inFlight && existing.promise) {
        console.warn(`[Throttle] Waiting for in-flight request to ${key}`);
        return Promise.reject({
          __throttled: true,
          message: `Waiting for in-flight request to ${key}`,
          waitFor: existing.promise,
        });
      }

      // Only throttle if request was recently completed (not in-flight)
      if (existing && now - existing.lastCall < THROTTLE_WINDOW && !existing.inFlight) {
        console.warn(`[Throttle] Request to ${key} was recently completed, using cache`);
      }

      // Track this request with a deferred promise that resolves with the actual response
      let resolve!: (value: any) => void;
      let reject!: (reason?: any) => void;
      const deferredPromise = new Promise<any>((res, rej) => { resolve = res; reject = rej; });
      requestThrottleMap.set(key, { lastCall: now, inFlight: true, promise: deferredPromise, resolve, reject });
    }

    // Limit concurrent requests to prevent resource exhaustion
    if (requestCount >= MAX_CONCURRENT_REQUESTS) {
      return Promise.reject(new Error("Too many concurrent requests"));
    }
    requestCount++;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle errors gracefully
axios.interceptors.response.use(
  (response) => {
    requestCount = Math.max(0, requestCount - 1);

    // Resolve waiting callers with the actual response, then clear the deferred
    if (response.config.url) {
      const key = `${response.config.method || "GET"}-${response.config.url}`;
      const existing = requestThrottleMap.get(key);
      if (existing) {
        existing.resolve?.(response);
        existing.inFlight = false;
        existing.promise = undefined;
        existing.resolve = undefined;
        existing.reject = undefined;
        requestThrottleMap.set(key, existing);
      }
    }

    return response;
  },
  (error) => {
    requestCount = Math.max(0, requestCount - 1);

    // Handle throttled requests - wait for the in-flight promise
    if (error.__throttled && error.waitFor) {
      console.log("[Throttle] Waiting for in-flight request to complete...");
      return error.waitFor;
    }

    // Reject waiting callers and clear the entry
    if (error.config?.url) {
      const key = `${error.config.method || "GET"}-${error.config.url}`;
      const existing = requestThrottleMap.get(key);
      existing?.reject?.(error);
      requestThrottleMap.delete(key);
    }

    // Handle 429 Too Many Requests — stop retrying, don't trigger auth refresh
    if (error.response?.status === 429) {
      const url = error.config?.url || "unknown";
      console.warn(`[RateLimit] 429 on ${url} — backing off`);
      error.config.__skipAuthRefresh = true;
      return Promise.reject(error);
    }

    // Handle CORS and network errors gracefully
    if (
      error.code === "NETWORK_ERROR" ||
      error.code === "ERR_INSUFFICIENT_RESOURCES" ||
      error.message?.includes("CORS") ||
      error.response?.status === 0
    ) {
      console.warn("Network/CORS error occurred, using fallback behavior");
      return Promise.reject(new Error("Network unavailable"));
    }

    // Handle 404 errors gracefully
    if (error.response?.status === 404) {
      console.warn("API endpoint not found:", error.config?.url);
      return Promise.reject(new Error("API endpoint not available"));
    }

    return Promise.reject(error);
  },
);

// Intercept responses to transform ISO dates to JS date objects
axios.interceptors.response.use(
  (response) => {
    const transformedResponse = deepSearchAndParseDates(response.data, ["createdAt", "updatedAt"]);
    return { ...response, data: transformedResponse };
  },
  (error) => {
    const message = error.response?.data?.message as ErrorMessage;
    const description = translateError(message);
    const isUnauthorizedError = error.response?.status === 401;
    const isForbiddenError = error.response?.status === 403;

    // Skip showing toast for auth errors (401/403) - these are handled by auth flow
    const isLogoutRequest = error.config?.url === "/auth/logout";
    const isUserMeRequest = error.config?.url === "/user/me";
    const shouldSkipToast =
      isLogoutRequest || isUserMeRequest || isUnauthorizedError || isForbiddenError;

    if (description && !shouldSkipToast) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description,
      });
    }

    // IMPORTANT: Preserve the original axios error structure for proper error handling
    // Don't wrap in new Error() as it loses response.status needed for auth checks
    return Promise.reject(error);
  },
);

// Create another instance to handle failed refresh tokens
// Reference: https://github.com/Flyrell/axios-auth-refresh/issues/191
const axiosForRefresh = _axios.create({ baseURL: apiBaseURL, withCredentials: true });

// Interceptor to handle expired access token errors
const handleAuthError = () => refreshToken(axiosForRefresh);

// Interceptor to handle expired refresh token errors
const handleRefreshError = async () => {
  // Only clear auth state and throw if the user was actually logged in.
  // For guests, silently reject — throwing here crashes Suspense boundaries (React Error #185).
  const { useAuthStore } = await import("@/client/stores/auth");
  const isLoggedIn = !!useAuthStore.getState().user;

  if (isLoggedIn) {
    console.warn("[Auth] Refresh token expired, clearing auth state");
    await queryClient.invalidateQueries({ queryKey: USER_KEY });
    throw new Error("Session expired. Please login again.");
  }

  // Guest user — reject with a user-friendly message instead of raw error
  return Promise.reject(new Error("Please sign up or log in to continue."));
};

// Add a request interceptor to bypass token refresh for certain requests
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url;
    const status = error.response?.status;

    // NEVER trigger auth refresh for 400 Bad Request errors
    // These are business logic errors (like resume limits), not auth errors
    if (status === 400) {
      console.log(
        `[Axios] Skipping token refresh for ${url} (400 Bad Request - business logic error)`,
      );
      error.config.__skipAuthRefresh = true;
      return Promise.reject(error);
    }

    // Skip token refresh for:
    // - Auth endpoints (login, register, logout don't need token refresh)
    // - /user/me requests (expected to fail for guests)
    // - Guest session endpoints (don't need auth)
    // - Public endpoints
    const skipRefreshUrls = [
      "/auth/logout",
      "/auth/login",
      "/auth/register",
      "/auth/providers",
      "/auth/forgot-password",
      "/auth/reset-password",
      "/user/me",
      "/guest/session",
      "/guest/tokens",
      "/feature/",
      "/payment/config",
      "/resume", // Resume endpoints can fail for guests - don't trigger refresh
      "/subscription-info", // User subscription info can fail for public users - don't trigger refresh (matches /user/{id}/subscription-info)
    ];
    
    // Check if URL should skip refresh - handle both exact matches and pattern matches
    const shouldSkipRefresh = skipRefreshUrls.some((skipUrl) => {
      if (!url) return false;
      // For subscription-info, also check if it's part of a user endpoint
      if (skipUrl === "/subscription-info") {
        return url.includes("/subscription-info");
      }
      return url.includes(skipUrl);
    });

    if (shouldSkipRefresh && (status === 401 || status === 403)) {
      // Mark this request to skip auth refresh
      console.log(`[Axios] Skipping token refresh for ${url} (${status})`);
      error.config.__skipAuthRefresh = true;
      return Promise.reject(error);
    }
    // Let other errors propagate to the auth refresh interceptor
    return Promise.reject(error);
  },
);

// Intercept responses to check for 401 and 403 errors, refresh token and retry the request
createAuthRefreshInterceptor(axios, handleAuthError, {
  statusCodes: [401, 403],
  retryInstance: axios,
  shouldRefresh: (error) => {
    // Check if we've already tried to refresh for this request to avoid infinite loops
    // axios-auth-refresh handles some of this, but nested interceptors can still loop
    const config = error.config as any;
    if (config?._retryCount && config._retryCount >= 2) {
      console.error(`[AuthRefresh] Maximum retry attempts reached for ${config.url}`);
      return false;
    }

    const shouldSkip = !!error.config?.__skipAuthRefresh;
    if (shouldSkip) {
      console.log(`[AuthRefresh] Skipping refresh for ${error.config?.url}`);
    } else {
      console.log(`[AuthRefresh] Will try to refresh token for ${error.config?.url}`);
    }
    return !shouldSkip;
  },
});

// Interceptor to track retries and handle 429s from refresh
axios.interceptors.request.use((config: any) => {
  if (config._retry) {
    config._retryCount = (config._retryCount || 0) + 1;
  }
  return config;
});

createAuthRefreshInterceptor(axiosForRefresh, handleRefreshError);
